/**
 * Projectiles: ballistics with optional gravity and homing, swept impact against arena geometry,
 * barriers, the enemy capsule and hittable deployables, parry reflection, splash, and chaining.
 */
import { SIM_DT } from '../constants.js';
import { angleBetween, degToRad, vLength, vNormalize } from '../sim/math.js';
import { applyDamage, falloffFactor, parryFaces, pointPacket } from './damage.js';
import { damageDeployable, removeDeployable } from './deployable-damage.js';
import { refreshEffect } from './effects.js';
import { segmentCapsule } from './geometry.js';
import { centerOf, combatantCapsule, secondsToTicks } from './state.js';
import { defOf, emit, mech } from './tick.js';
import { otherSlot } from './types.js';
import { deployableHitCenter, deployableHitRadius, findDeployable } from './trace.js';
import { splashAt } from './weapons.js';
import { weaponOf } from './catalog.js';
function segmentSphere(a, b, c, r) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dz = b.z - a.z;
    const len = Math.hypot(dx, dy, dz);
    if (len < 1e-9)
        return null;
    const ux = dx / len;
    const uy = dy / len;
    const uz = dz / len;
    const lx = a.x - c.x;
    const ly = a.y - c.y;
    const lz = a.z - c.z;
    const bq = lx * ux + ly * uy + lz * uz;
    const cq = lx * lx + ly * ly + lz * lz - r * r;
    const disc = bq * bq - cq;
    if (disc < 0)
        return null;
    const t = -bq - Math.sqrt(disc);
    if (t < 0 || t > len)
        return null;
    return t / len;
}
function outOfBounds(t, p) {
    const b = t.ctx.arena.bounds;
    const margin = 20;
    return (p.x < b.min.x - margin ||
        p.x > b.max.x + margin ||
        p.z < b.min.z - margin ||
        p.z > b.max.z + margin ||
        p.y < b.min.y - margin ||
        p.y > b.max.y + 200);
}
function impactEvent(t, p, position, normal, hit) {
    emit(t, { t: 'projectileImpact', id: p.id, position: { ...position }, normal, hit });
}
function explode(t, p, point) {
    const weapon = weaponOf(t.ctx, p.weapon);
    const splash = weapon.projectile?.splash;
    if (!splash)
        return;
    splashAt(t, p.slot, point, splash.radius, splash.damage * p.damageScale, splash.minFactor, splash.selfDamage);
}
function chain(t, p, from) {
    const weapon = weaponOf(t.ctx, p.weapon);
    const spec = weapon.projectile?.chain;
    if (!spec || p.chainsLeft <= 0)
        return;
    let jumps = 0;
    for (const d of [...t.s.deployables]) {
        if (d.slot === p.slot || d.maxHp <= 0 || jumps >= p.chainsLeft)
            continue;
        const dist = Math.hypot(d.position.x - from.x, d.position.y - from.y, d.position.z - from.z);
        if (dist > spec.range)
            continue;
        damageDeployable(t, d, weapon.damage * spec.damageFactor * p.damageScale, p.slot);
        emit(t, {
            t: 'beam',
            slot: p.slot,
            weapon: weapon.id,
            origin: { ...from },
            end: { ...d.position },
            hit: null,
        });
        jumps++;
    }
    p.chainsLeft = 0;
}
export function stepProjectiles(t) {
    const survivors = [];
    for (const p of t.s.projectiles) {
        const weapon = weaponOf(t.ctx, p.weapon);
        const spec = weapon.projectile;
        p.ticksLeft--;
        if (p.ticksLeft < 0 || outOfBounds(t, p.position)) {
            impactEvent(t, p, p.position, null, null);
            continue;
        }
        // Homing: bend the velocity toward the target's centre by at most the turn rate.
        if (spec.homing && p.homingTarget !== null) {
            const target = mech(t, p.homingTarget);
            if (target.alive) {
                const speed = vLength(p.velocity);
                const dir = vNormalize(p.velocity);
                const aim = centerOf(target.body, defOf(t, p.homingTarget));
                const want = vNormalize({
                    x: aim.x - p.position.x,
                    y: aim.y - p.position.y,
                    z: aim.z - p.position.z,
                });
                const angle = angleBetween(dir, want);
                const maxTurn = degToRad(spec.homing.turnRateDeg) * SIM_DT;
                let next = want;
                if (angle > maxTurn && angle > 1e-6) {
                    const k = maxTurn / angle;
                    next = vNormalize({
                        x: dir.x + (want.x - dir.x) * k,
                        y: dir.y + (want.y - dir.y) * k,
                        z: dir.z + (want.z - dir.z) * k,
                    });
                }
                p.velocity = { x: next.x * speed, y: next.y * speed, z: next.z * speed };
            }
        }
        p.velocity.y -= spec.gravity * SIM_DT;
        const from = p.position;
        const to = {
            x: from.x + p.velocity.x * SIM_DT,
            y: from.y + p.velocity.y * SIM_DT,
            z: from.z + p.velocity.z * SIM_DT,
        };
        const stepLen = Math.hypot(to.x - from.x, to.y - from.y, to.z - from.z);
        const dir = vNormalize({ x: to.x - from.x, y: to.y - from.y, z: to.z - from.z });
        // Nearest of: arena/barrier, ground-target height, enemy capsule, hittable deployables.
        let best = 1.0000001;
        let kind = 'none';
        let normal = null;
        let struck = null;
        const arena = stepLen > 1e-9 ? t.world.castRayHit(from, dir, stepLen + spec.radius) : null;
        if (arena) {
            best = Math.max(0, (arena.distance - spec.radius) / Math.max(stepLen, 1e-9));
            kind = 'arena';
            normal = arena.normal;
            struck = findDeployable(t, arena.blockerId);
        }
        if (p.groundY !== null && to.y <= p.groundY && from.y > p.groundY) {
            const f = (from.y - p.groundY) / (from.y - to.y);
            if (f < best) {
                best = f;
                kind = 'ground';
                normal = { x: 0, y: 1, z: 0 };
                struck = null;
            }
        }
        const enemy = otherSlot(p.slot);
        const target = mech(t, enemy);
        if (target.alive) {
            const cap = combatantCapsule(target, defOf(t, enemy));
            const widened = {
                base: cap.base,
                radius: cap.radius + spec.radius,
                halfHeight: cap.halfHeight,
            };
            const f = segmentCapsule(from, to, widened);
            if (f !== null && f < best) {
                best = f;
                kind = 'mech';
                normal = null;
                struck = null;
            }
        }
        for (const d of t.s.deployables) {
            if (d.slot === p.slot)
                continue;
            const r = deployableHitRadius(d);
            if (r <= 0)
                continue;
            const f = segmentSphere(from, to, deployableHitCenter(d), r + spec.radius);
            if (f !== null && f < best) {
                best = f;
                kind = 'deployable';
                normal = null;
                struck = d;
            }
        }
        if (kind === 'none') {
            p.position = to;
            survivors.push(p);
            continue;
        }
        const point = {
            x: from.x + (to.x - from.x) * best,
            y: from.y + (to.y - from.y) * best,
            z: from.z + (to.z - from.z) * best,
        };
        if (kind === 'mech') {
            // A facing parry reflects the projectile back at whoever fired it.
            if (parryFaces(t, enemy, from)) {
                const shooter = p.slot;
                const back = centerOf(mech(t, shooter).body, defOf(t, shooter));
                const speed = vLength(p.velocity);
                const rd = vNormalize({ x: back.x - point.x, y: back.y - point.y, z: back.z - point.z });
                p.velocity = { x: rd.x * speed, y: rd.y * speed, z: rd.z * speed };
                p.position = point;
                p.slot = enemy;
                p.reflected = true;
                p.homingTarget = shooter;
                p.ticksLeft = Math.max(p.ticksLeft, secondsToTicks(2));
                emit(t, { t: 'parry', slot: enemy, position: { ...point } });
                survivors.push(p);
                continue;
            }
            const age = secondsToTicks(spec.lifeSeconds) - p.ticksLeft;
            const travelled = age * SIM_DT * spec.speed;
            const amount = weapon.damage * falloffFactor(travelled, weapon.range) * p.damageScale;
            applyDamage(t, pointPacket(t, enemy, p.slot, amount, 'projectile', point, from));
            for (const effect of weapon.onHit) {
                const pr = effect.params;
                refreshEffect(t, enemy, effect.kind, p.slot, secondsToTicks(effect.seconds), {
                    ...(pr.speedScale !== undefined ? { speedScale: pr.speedScale } : {}),
                    ...(pr.dot ? { dot: { damagePerSecond: pr.dot.damagePerSecond, kind: 'burn' } } : {}),
                });
            }
            t.s.stats[p.slot].shotsHit++;
            impactEvent(t, p, point, null, enemy);
            explode(t, p, point);
            chain(t, p, point);
            continue;
        }
        if (kind === 'deployable' && struck) {
            damageDeployable(t, struck, weapon.damage * p.damageScale, p.slot);
            impactEvent(t, p, point, null, null);
            explode(t, p, point);
            continue;
        }
        if (kind === 'arena' && struck) {
            // A barrier: damage it, then splash.
            damageDeployable(t, struck, weapon.damage * p.damageScale, p.slot);
            if (struck.hp <= 0 && t.s.deployables.includes(struck))
                removeDeployable(t, struck);
        }
        impactEvent(t, p, point, normal, null);
        explode(t, p, point);
        chain(t, p, point);
    }
    t.s.projectiles = survivors;
}
//# sourceMappingURL=projectiles.js.map